import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { CartItemsComponent } from '@theme/component-cart-items';

/**
 * Optional Köpskydd add-on in the cart summary.
 *
 * The server-rendered markup is the source of truth (data-quantity, data-keys, data-goods).
 * This element only mutates the cart, in one /cart/update.js request that also returns the
 * cart sections, and lets Horizon's cart-items-component morph them:
 * - checking sets exactly one unit, unchecking removes every Köpskydd line;
 * - more than one unit or line is reduced to one;
 * - Köpskydd without any other product in the cart is removed.
 */
class VindelyKopskydd extends HTMLElement {
  static observedAttributes = ['data-quantity', 'data-keys', 'data-goods'];

  /** Shared by every instance (drawer and cart page) so two updates never race. */
  static #pending = false;

  /** True when a normalisation was skipped because an update was running. */
  static #skipped = false;

  #normalizeTimer = 0;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#scheduleNormalize();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    clearTimeout(this.#normalizeTimer);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#scheduleNormalize();
  }

  get #keys() {
    return (this.dataset.keys || '').split(',').filter(Boolean);
  }

  get #quantity() {
    return Number(this.dataset.quantity) || 0;
  }

  get #goods() {
    return Number(this.dataset.goods) || 0;
  }

  /** Waits until a morph has updated every data attribute before comparing them. */
  #scheduleNormalize() {
    clearTimeout(this.#normalizeTimer);
    this.#normalizeTimer = setTimeout(() => this.#normalize(), 0);
  }

  #normalize() {
    if (this.#quantity === 0) return;
    if (VindelyKopskydd.#pending) {
      VindelyKopskydd.#skipped = true;
      return;
    }
    if (this.#goods === 0) {
      this.#apply(0);
    } else if (this.#quantity > 1 || this.#keys.length > 1) {
      this.#apply(1);
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;

    if (VindelyKopskydd.#pending) {
      input.checked = !input.checked;
      return;
    }
    this.#apply(input.checked ? 1 : 0);
  };

  #cartComponents() {
    return Array.from(document.querySelectorAll('cart-items-component')).filter(
      (element) => element instanceof HTMLElement && element.dataset.sectionId
    );
  }

  /**
   * Locks the cart like Horizon does during its own quantity updates. The checkbox keeps
   * its focus (aria-disabled instead of disabled) so keyboard users stay in place.
   * @param {boolean} busy
   */
  #setBusy(busy) {
    VindelyKopskydd.#pending = busy;
    for (const component of this.#cartComponents()) component.classList.toggle('cart-items-disabled', busy);
    for (const element of document.querySelectorAll('vindely-kopskydd')) {
      element.setAttribute('aria-busy', String(busy));
      element.querySelector('input[type="checkbox"]')?.setAttribute('aria-disabled', String(busy));
    }
  }

  /** @param {boolean} visible */
  #showError(visible) {
    const error = this.querySelector('[data-kopskydd-error]');
    if (error instanceof HTMLElement) error.hidden = !visible;
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    return response.json();
  }

  /** @param {number} target - Wanted Köpskydd quantity, 0 or 1. */
  async #apply(target) {
    if (VindelyKopskydd.#pending) return;
    this.#setBusy(true);
    this.#showError(false);

    // Never mutate on top of a running Horizon quantity update; its result is re-read below.
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);

    const keys = this.#keys;
    const current = this.#quantity;
    const adding = target > 0 && current === 0;
    if (!adding && target === current && keys.length <= 1) {
      this.#setBusy(false);
      return;
    }

    const variantId = String(this.dataset.variantId);
    const updates = adding
      ? { [variantId]: 1 }
      : Object.fromEntries(keys.map((key, index) => [key, index === 0 ? target : 0]));
    const body = {
      updates,
      sections: this.#cartComponents()
        .map((element) => element.dataset.sectionId)
        .join(','),
      sections_url: window.location.pathname,
    };

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: adding ? 'add' : target === 0 ? 'remove' : 'update',
        context: 'cart',
        lines: adding
          ? [{ merchandiseId: variantId, quantity: 1 }]
          : keys.map((id, index) => ({ id, quantity: index === 0 ? target : 0 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body: JSON.stringify(body) }));
      const cart = await response.json();
      if (!response.ok || cart.status || cart.errors) {
        throw new Error(cart.description || cart.message || cart.errors || 'Cart update failed');
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'vindely-kopskydd',
          didError: false,
        },
      });
    } catch (error) {
      const input = this.querySelector('input[type="checkbox"]');
      if (input instanceof HTMLInputElement) input.checked = current > 0;
      this.#showError(true);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Köpskydd update failed', code: 'INVALID' }));
      try {
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, source: 'vindely-kopskydd', didError: true },
        });
      } catch (readError) {
        deferred.reject(readError);
      }
    } finally {
      this.#setBusy(false);
      // A cart change made while this request ran skipped normalising; check again once the sections have morphed.
      if (VindelyKopskydd.#skipped) {
        VindelyKopskydd.#skipped = false;
        setTimeout(() => this.#scheduleNormalize(), 300);
      }
    }
  }
}

if (!customElements.get('vindely-kopskydd')) {
  customElements.define('vindely-kopskydd', VindelyKopskydd);
}
